import { SitemapEntry } from '../sitemap.types';

// This catalog is intentionally isolated from normal feature and release work.
// Agents may edit it only during an explicitly requested sitemap update run.
export const ADMIN_STUDENT_SITEMAP_ENTRIES = [
	{
		id: 'manage-students',
		title: 'Manage students',
		guidance:
			'Search for students by name, identifier, email, or student or contact phone number; add or edit a student; and open the selected student’s activity workspace.',
		href: '/app/organizations/students/manage',
		application: 'admin',
		audiences: ['customer', 'admin'],
		categoryPath: ['Students', 'Management'],
		searchTerms: [
			'student roster',
			'find student',
			'add student',
			'edit student',
			'student activity',
			'contact phone search',
			'find student by contact phone',
			'parent phone number',
		],
	},
	{
		id: 'import-students-in-bulk',
		title: 'Import students in bulk',
		guidance:
			'Download the CSV template, upload a student list, correct validation errors, and run the import.',
		href: '/app/organizations/students/massupload',
		application: 'admin',
		audiences: ['customer', 'admin'],
		categoryPath: ['Students', 'Management', 'Bulk Import'],
		searchTerms: [
			'bulk add students',
			'mass upload',
			'csv template',
			'upload students',
			'import errors',
		],
	},
	{
		id: 'update-students-in-bulk',
		title: 'Update students in bulk',
		guidance:
			'Select students in Manage Students, then update grades, test eligibility, tags, settings, courses, course end dates, or transcripts; or re-enroll, transfer, withdraw, or activate the selected students.',
		href: '/app/organizations/students/manage',
		application: 'admin',
		audiences: ['customer', 'admin'],
		categoryPath: ['Students', 'Management', 'Bulk Actions'],
		searchTerms: [
			'bulk edit students',
			'change grade level',
			'bulk tags',
			'bulk course',
			'bulk course end date',
			'course extension',
			'bulk transcript',
			'bulk re-enroll',
			'transfer students',
			'move students between programs',
			'activate selected students',
			'withdraw selected students',
		],
	},
	{
		id: 'review-student-overview',
		title: 'Review a student overview',
		guidance:
			'Find and open the student, then use Overview to review credits, pace, GPA, course counts, recent contact, classroom schedule, Meetup minutes, and last-online information.',
		href: '/app/organizations/students/manage',
		application: 'admin',
		audiences: ['customer', 'admin'],
		categoryPath: ['Students', 'Student Activity', 'Overview'],
		searchTerms: [
			'at a glance',
			'student pace',
			'gpa',
			'credits',
			'classroom schedule',
			'last online',
			'completed this month',
		],
	},
	{
		id: 'manage-student-alerts',
		title: 'Review and act on student alerts',
		guidance:
			'Find and open the student, then review alerts for data changes, state tests, pause, withdrawal, graduation, Meetup gates, and funding warnings.',
		href: '/app/organizations/students/manage',
		application: 'admin',
		audiences: ['customer', 'admin'],
		categoryPath: ['Students', 'Student Activity', 'Alerts'],
		searchTerms: [
			'student notification',
			'attention panel',
			'pending request',
			'test registration alert',
			'funding warning',
		],
	},
	{
		id: 'review-student-courses',
		title: 'Review a student’s courses',
		guidance:
			'Find and open the student, then use Overview or Completed & Tests to review current, previous, planned, persistent, and completed courses.',
		href: '/app/organizations/students/manage',
		application: 'admin',
		audiences: ['customer', 'admin'],
		categoryPath: ['Students', 'Courses', 'Overview'],
		searchTerms: [
			'current courses',
			'past courses',
			'planned courses',
			'persistent courses',
			'completed courses',
			'course certificate',
			'teacher contact',
		],
	},
	{
		id: 'request-course-for-student',
		title: 'Request a course for a student',
		guidance:
			'Find and open the student, then use the Courses area to create or review a request for the selected student.',
		href: '/app/organizations/students/manage',
		application: 'admin',
		audiences: ['customer', 'admin'],
		categoryPath: ['Students', 'Courses', 'Requests'],
		searchTerms: [
			'create course request',
			'student course request',
			'request registration',
			'planned course request',
		],
	},
	{
		id: 'review-course-requests',
		title: 'Review student course requests',
		guidance:
			'Review the course-request queue, open a request or student, and complete the permitted request actions.',
		href: '/app/course-requests',
		application: 'admin',
		audiences: ['admin'],
		categoryPath: ['Students', 'Courses', 'Requests'],
		searchTerms: [
			'course request queue',
			'pending course request',
			'view course request',
			'registration creation request',
		],
	},
	{
		id: 'manage-student-course-registration',
		title: 'Manage a student’s course registration',
		guidance:
			'Find and open the student, then add or edit a registration, extend, withdraw, or finalize a course, recover grades, copy a past course, or reactivate an eligible course.',
		href: '/app/organizations/students/manage',
		application: 'admin',
		audiences: ['admin'],
		categoryPath: ['Students', 'Courses', 'Registration'],
		searchTerms: [
			'add course',
			'register course',
			'edit registration',
			'course extension',
			'withdraw course',
			'finalize course',
			'reactivate course',
			'reopen course',
			'restore coursework',
			'copy course',
			'recover grades',
		],
	},
	{
		id: 'review-student-course-progress',
		title: 'Review one student’s course progress',
		guidance:
			'Find and open the student, then open a current, previous, or completed course progress report, notes, gradebook, or LMS section to investigate progress or grade differences and, when permitted, excuse or un-excuse eligible assignments.',
		href: '/app/organizations/students/manage',
		application: 'admin',
		audiences: ['customer', 'admin'],
		categoryPath: ['Students', 'Courses', 'Progress'],
		searchTerms: [
			'student grades',
			'course progress report',
			'grade mismatch',
			'missing grade',
			'student notes',
			'gradebook',
			'Excuse/Reset',
			'Excuse Assignment',
			'Un-excuse',
			'Excused assignment limit reached',
			'Teacher graded assignment excused limit reached',
			'lms section',
			'extension notes',
			'course teacher',
		],
	},
	{
		id: 'manage-student-course-exam-access',
		title: 'Manage a student’s course exam access',
		guidance:
			'Find and open the student, then open the current course to lock or unlock its final exam or view the configured exam or proctor password.',
		href: '/app/organizations/students/manage',
		application: 'admin',
		audiences: ['admin'],
		categoryPath: ['Students', 'Courses', 'Exams'],
		searchTerms: [
			'exam lock',
			'unlock final exam',
			'proctor password',
			'exam password',
		],
	},
	{
		id: 'review-student-course-activity',
		title: 'Review a student’s course activity',
		guidance:
			'Find and open the student, then use Course Activity to filter activity and time spent or generate and print a course-activity report.',
		href: '/app/organizations/students/manage',
		application: 'admin',
		audiences: ['customer', 'admin'],
		categoryPath: ['Students', 'Courses', 'Activity'],
		searchTerms: [
			'time spent',
			'activity report',
			'print activity',
			'course activity types',
			'activity date range',
		],
	},
	{
		id: 'manage-student-information',
		title: 'Manage a student’s personal information',
		guidance:
			'Find and open the student, then use Student Info to review edit history and update names, identity fields, phone numbers, email, address, timezone, or residence information.',
		href: '/app/organizations/students/manage',
		application: 'admin',
		audiences: ['customer', 'admin'],
		categoryPath: ['Students', 'Information', 'Personal'],
		searchTerms: [
			'personal information',
			'preferred name',
			'legal name',
			'student phone',
			'student email',
			'student address',
			'edit history',
		],
	},
	{
		id: 'manage-student-school-information',
		title: 'Manage a student’s school and program information',
		guidance:
			'Find and open the student, then use Student Info to review school and program context and update permitted cohort, identifier, grade, site, track, graduation-plan, course-limit, attendance-schedule, billing, or memo fields.',
		href: '/app/organizations/students/manage',
		application: 'admin',
		audiences: ['customer', 'admin'],
		categoryPath: ['Students', 'Information', 'School and Program'],
		searchTerms: [
			'cohort year',
			'district id',
			'state id',
			'grade level',
			'program tier',
			'student site',
			'program track',
			'graduation plan',
			'concurrent course limit',
			'attendance schedule',
			'billing level',
			'student memo',
		],
	},
	{
		id: 'manage-student-contacts',
		title: 'Manage a student’s contacts and guardians',
		guidance:
			'Find and open the student, then use Contacts to add, edit, or remove family contacts; manage phone, email, guardian, SMS, and parent-portal account details; or open a parent profile to review its assigned students.',
		href: '/app/organizations/students/manage',
		application: 'admin',
		audiences: ['customer', 'admin'],
		categoryPath: ['Students', 'Contacts'],
		searchTerms: [
			'parent contact',
			'guardian',
			'family contact',
			'contact phone',
			'contact email',
			'primary guardian',
			'guardian account',
			'link guardian',
			'parent portal access',
			'parent assigned students',
			'linked students',
		],
	},
	{
		id: 'manage-student-tags-settings',
		title: 'Manage a student’s tags and settings',
		guidance:
			'Find and open the student, then use Tags & Settings to add or remove tags and manage student-specific setting overrides.',
		href: '/app/organizations/students/manage',
		application: 'admin',
		audiences: ['admin'],
		categoryPath: ['Students', 'Settings'],
		searchTerms: [
			'student tag',
			'create tag',
			'student property',
			'setting override',
			'toggle setting',
		],
	},
	{
		id: 'manage-student-account-access',
		title: 'Manage a student’s account and password',
		guidance:
			'Find and open the student, then use Account Info to change the account email or reset the student portal or laptop/Google password.',
		href: '/app/organizations/students/manage',
		application: 'admin',
		audiences: ['admin', 'superAdmin'],
		categoryPath: ['Students', 'Account Access'],
		searchTerms: [
			'student account',
			'student password',
			'forgot password',
			'reset portal password',
			'portal login',
			'google password',
			'chromebook password',
			'laptop password',
			'account email',
		],
	},
	{
		id: 'manage-student-enrollment-status',
		title: 'Manage a student’s enrollment status',
		guidance:
			'Find and open the student, then use Status to review the enrollment timeline and permitted pause, withdrawal, re-entry, activation, or graduation actions and requests.',
		href: '/app/organizations/students/manage',
		application: 'admin',
		audiences: ['admin'],
		categoryPath: ['Students', 'Enrollment', 'Status'],
		searchTerms: [
			'pause student',
			'unpause student',
			'withdraw student',
			'unenroll student',
			'reentry request',
			're-enroll student',
			'restore enrollment',
			'activate student',
			'unactivate student',
			'graduation review',
			'enrollment timeline',
		],
	},
	{
		id: 'complete-student-enrollment-checklist',
		title: 'Complete a student enrollment checklist',
		guidance:
			'Find and open the student, then use Enrollment to complete registration and activation requirements, upload missing files, add contacts, enroll classes, or request a laptop.',
		href: '/app/organizations/students/manage',
		application: 'admin',
		audiences: ['customer', 'admin'],
		categoryPath: ['Students', 'Enrollment', 'Checklist'],
		searchTerms: [
			'registration checklist',
			'activation checklist',
			'manual activation',
			'activation file',
			'override requirement',
			'enrollment requirement',
		],
	},
	{
		id: 'manage-student-paperwork',
		title: 'Manage paperwork for one student',
		guidance:
			'Find and open the student, then use Enrollment to upload, forward, waive, tag, expire, delete, or review the student’s paperwork and proof.',
		href: '/app/organizations/students/manage',
		application: 'admin',
		audiences: ['customer', 'admin'],
		categoryPath: ['Students', 'Paperwork', 'Individual Student'],
		searchTerms: [
			'upload paperwork',
			'pending paperwork',
			'waive paperwork',
			'expire paperwork',
			'paperwork tags',
			'paperwork history',
		],
	},
	{
		id: 'send-student-paperwork-request',
		title: 'Send electronic paperwork to a student',
		guidance:
			'Find and open the student, then create an electronic paperwork request and review its delivery or signature status, audit document, signing link, or recall action.',
		href: '/app/organizations/students/manage',
		application: 'admin',
		audiences: ['customer', 'admin'],
		categoryPath: ['Students', 'Paperwork', 'Requests'],
		searchTerms: [
			'send enrollment paperwork',
			'adobe sign',
			'electronic signature',
			'paperwork recipient',
			'paperwork request history',
			'signature status',
			'paperwork delivery audit',
			'copy signing link',
			'recall paperwork',
		],
	},
	{
		id: 'request-student-school-records',
		title: 'Request school records for a student',
		guidance:
			'Find and open the student, then send a school-records request by fax or email and optionally include an IEP request.',
		href: '/app/organizations/students/manage',
		application: 'admin',
		audiences: ['customer', 'admin'],
		categoryPath: ['Students', 'Records', 'School Records'],
		searchTerms: [
			'records request',
			'previous school',
			'fax records',
			'email records',
			'iep request',
		],
	},
	{
		id: 'review-student-payment-orders',
		title: 'Review a student’s payment and order status',
		guidance:
			'Find and open the student, then use Enrollment payment information to review Shopify orders or open the permitted Paywhirl customer actions.',
		href: '/app/organizations/students/manage',
		application: 'admin',
		audiences: ['admin'],
		categoryPath: ['Students', 'Enrollment', 'Payments'],
		searchTerms: [
			'shopify order',
			'payment history',
			'paywhirl customer',
			'payment status',
			'enable payment',
			'ignore payment',
		],
	},
	{
		id: 'manage-student-learning-plan',
		title: 'Manage a student’s learning plan',
		guidance:
			'Find and open the student, then review requirements and credits; assign, rebuild, lock, edit, exempt, audit, download, or view the learning plan and report card as permitted.',
		href: '/app/organizations/students/manage',
		application: 'admin',
		audiences: ['customer', 'admin'],
		categoryPath: ['Students', 'Learning Plans'],
		searchTerms: [
			'graduation requirements',
			'earned credits',
			'graduation plan',
			'lock learning plan',
			'requirement exemption',
			'plan history',
			'plan pdf',
			'download learning plan',
			'report card',
			'written learning plan',
		],
	},
	{
		id: 'schedule-student-learning-plan',
		title: 'Plan and schedule a student’s courses',
		guidance:
			'Find and open the student, then use Learning Plan to add, remove, replace, reorder, schedule, or register sections and work with Pathfinder suggestions.',
		href: '/app/organizations/students/manage',
		application: 'admin',
		audiences: ['admin'],
		categoryPath: ['Students', 'Learning Plans', 'Course Planning'],
		searchTerms: [
			'pathfinder',
			'suggest plan',
			'ai scheduler',
			'planned section',
			'replace section',
			'schedule course',
			'plan course',
			'course catalog',
		],
	},
	{
		id: 'manage-student-transcripts',
		title: 'Manage a student’s transcripts',
		guidance:
			'Find and open the student, then upload or generate a transcript, edit course mappings and credits, verify records, manage source schools, review version history, or run a plan audit.',
		href: '/app/organizations/students/manage',
		application: 'admin',
		audiences: ['customer', 'admin'],
		categoryPath: ['Students', 'Records', 'Transcripts'],
		searchTerms: [
			'upload transcript',
			'expl transcript',
			'transcript entry',
			'transcript checklist',
			'course mapping',
			'verify credits',
			'credit verification',
			'transcript school',
			'source school',
			'transcript version history',
			'plan audit',
			'original transcript',
		],
	},
	{
		id: 'request-official-student-transcript',
		title: 'Request or download a student transcript',
		guidance:
			'Find and open the student, then use Transcript Entry to view or download an unofficial transcript and find official-transcript ordering or contact options.',
		href: '/app/organizations/students/manage',
		application: 'admin',
		audiences: ['customer', 'admin'],
		categoryPath: ['Students', 'Records', 'Transcript Requests'],
		searchTerms: [
			'official transcript',
			'unofficial transcript',
			'download transcript',
			'order transcript',
			'transcript phone',
		],
	},
	{
		id: 'manage-student-test-results',
		title: 'Manage a student’s test history and results',
		guidance:
			'Find and open the student, then use Completed & Tests to review STAR or state-test status and history, including required and completed indicators, and edit eligibility, notes, attempts, scores, or the primary result.',
		href: '/app/organizations/students/manage',
		application: 'admin',
		audiences: ['customer', 'admin'],
		categoryPath: ['Students', 'Testing'],
		searchTerms: [
			'state test',
			'star assessment',
			'test eligibility',
			'test attempt',
			'additional test attempt',
			'test score',
			'primary result',
			'test history',
			'testing summary',
			'tests still needed',
			'pending testing',
		],
	},
	{
		id: 'manage-student-events-tasks',
		title: 'Manage a student’s events and tasks',
		guidance:
			'Find and open the student, then use Events & Tasks to review contact logs and reminders or create, edit, view, and complete student events and tasks.',
		href: '/app/organizations/students/manage',
		application: 'admin',
		audiences: ['customer', 'admin'],
		categoryPath: ['Students', 'Events and Tasks'],
		searchTerms: [
			'contact event',
			'contact log',
			'student task',
			'complete task',
			'automated reminder',
			'last contact',
		],
	},
	{
		id: 'manage-student-announcements',
		title: 'Manage announcements for a student',
		guidance:
			'Find and open the student, then use Announcements to review or dismiss assigned messages and create, edit, or remove personal announcements.',
		href: '/app/organizations/students/manage',
		application: 'admin',
		audiences: ['customer', 'admin'],
		categoryPath: ['Students', 'Communication', 'Announcements'],
		searchTerms: [
			'personal announcement',
			'student message',
			'dismiss announcement',
			'edit announcement',
		],
	},
	{
		id: 'manage-student-career-placement',
		title: 'Manage a student’s career plan and placement',
		guidance:
			'Find and open the student, then use Career & Pathway to review or edit college intent, career goals, credentials, notes, and graduate college or employment placement.',
		href: '/app/organizations/students/manage',
		application: 'admin',
		audiences: ['customer', 'admin'],
		categoryPath: ['Students', 'Career and Pathway'],
		searchTerms: [
			'career plan',
			'college intent',
			'college help',
			'industry credential',
			'graduate placement',
			'employment placement',
		],
	},
	{
		id: 'manage-student-support-insights',
		title: 'Manage student support insight notes',
		guidance:
			'Find and open the student, then use Support Insights to view, edit, archive, or review history for permitted role-specific support notes.',
		href: '/app/organizations/students/manage',
		application: 'admin',
		audiences: ['admin'],
		categoryPath: ['Students', 'Support', 'Insights'],
		searchTerms: [
			'additional comments',
			'academic coach notes',
			'local advocate notes',
			'enrollment counselor notes',
			'testing coordinator notes',
			'transcript coordinator notes',
			'note history',
		],
	},
	{
		id: 'review-student-response-forms',
		title: 'Review a student’s response forms',
		guidance:
			'Find and open the student, then use Response List to filter submitted forms, review response status, and provide permitted feedback.',
		href: '/app/organizations/students/manage',
		application: 'admin',
		audiences: ['admin'],
		categoryPath: ['Students', 'Support', 'Responses'],
		searchTerms: [
			'response list',
			'submitted response',
			'credentialed responder',
			'response feedback',
			'response status',
		],
	},
	{
		id: 'manage-student-milestones',
		title: 'Manage a student’s milestones',
		guidance:
			'Find and open the student, then use Milestones to review, assign, edit, or delete milestones and upload or view completion proof.',
		href: '/app/organizations/students/manage',
		application: 'admin',
		audiences: ['customer', 'admin'],
		categoryPath: ['Students', 'Progress', 'Milestones'],
		searchTerms: [
			'add milestone',
			'assign milestone',
			'milestone proof',
			'completed milestone',
		],
	},
	{
		id: 'manage-student-checklists',
		title: 'Manage a student’s progress checklists',
		guidance:
			'Find and open the student, then use Checklists to review assigned lists, mark actions complete or incomplete, and edit checklist assignments.',
		href: '/app/organizations/students/manage',
		application: 'admin',
		audiences: ['customer', 'admin'],
		categoryPath: ['Students', 'Progress', 'Checklists'],
		searchTerms: [
			'progress checklist',
			'complete checklist item',
			'assign checklist',
			'student checklist',
		],
	},
	{
		id: 'manage-student-laptop',
		title: 'Manage a student’s laptop or MiFi',
		guidance:
			'Find and open the student, then use Laptop Information to review assigned Chromebook or MiFi assets and request history, track shipments, or request equipment, a replacement, or a return as permitted.',
		href: '/app/organizations/students/manage',
		application: 'admin',
		audiences: ['admin'],
		categoryPath: ['Students', 'Support', 'Devices'],
		searchTerms: [
			'laptop request',
			'replacement laptop',
			'replacement chromebook',
			'mifi',
			'hotspot',
			'asset serial',
			'track shipment',
			'shipment tracking',
			'return laptop',
			'device return',
			'laptop request history',
		],
	},
	{
		id: 'manage-student-meetups',
		title: 'Manage a student’s Meetups',
		guidance:
			'Find and open the student, then use Meetup Info to review attendance, upcoming or available times, and virtual restrictions; log or edit eligible one-to-one Meetups; and manage permitted gate exemptions.',
		href: '/app/organizations/students/manage',
		application: 'admin',
		audiences: ['customer', 'admin'],
		categoryPath: ['Students', 'Meetups'],
		searchTerms: [
			'meetup gate',
			'virtual meetup restriction',
			'meetup exemption',
			'upcoming meetup',
			'available meetup',
			'meetup history',
			'meetup attendance',
			'meeting time',
			'log meetup',
			'edit meetup log',
		],
	},
	{
		id: 'contact-support-for-student',
		title: 'Contact support for a student',
		guidance:
			'Find and open the student, then use Contact Support to review the support roster, call or email a staff member, or open a staff profile.',
		href: '/app/organizations/students/manage',
		application: 'admin',
		audiences: ['customer', 'admin'],
		categoryPath: ['Students', 'Support', 'Contacts'],
		searchTerms: [
			'support contacts',
			'call support',
			'email support',
			'staff profile',
			'support roster',
		],
	},
	{
		id: 'manage-student-work-queues',
		title: 'Manage a student’s work queues',
		guidance:
			'Find and open the student, then use the attention panel to review and manage the student’s FOCUS and tracker queue items.',
		href: '/app/organizations/students/manage',
		application: 'admin',
		audiences: ['admin'],
		categoryPath: ['Students', 'Staff Work', 'Queues'],
		searchTerms: [
			'focus queue',
			'tracker queue',
			'work queue',
			'attention panel',
			'student workload',
		],
	},
	{
		id: 'troubleshoot-student-access',
		title: 'Troubleshoot a student’s access',
		guidance:
			'Find and open the student, then use permitted User Links, account tools, masquerade options, access-gate context, or classroom-schedule diagnostics to investigate an access problem.',
		href: '/app/organizations/students/manage',
		application: 'admin',
		audiences: ['superAdmin'],
		categoryPath: ['Students', 'Support', 'Troubleshooting'],
		searchTerms: [
			'user links',
			'portal access',
			'account lockout',
			'sign-in loop',
			'access gate',
			'masquerade as student',
			'super masquerade',
			'lms link',
			'classroom schedule debug',
		],
	},
	{
		id: 'customize-student-activity-workspace',
		title: 'Customize the Student Activity workspace',
		guidance:
			'Find and open a student, then use workspace settings to choose the page layout, review keyboard shortcuts, and reorder, reset, or save the widget layout.',
		href: '/app/organizations/students/manage',
		application: 'admin',
		audiences: ['customer', 'admin'],
		categoryPath: ['Students', 'Student Activity', 'Workspace'],
		searchTerms: [
			'student workspace',
			'lunchbox layout',
			'two panes',
			'widget layout',
			'reorder widgets',
			'reset widgets',
			'keyboard shortcuts',
		],
	},
] as const satisfies readonly SitemapEntry[];
